import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getComprasEstado, getComprasPedidos, getComprasFiltros, getComprasPedido,
  exportComprasPedidos,
} from '../api/client'
import { currentLocale } from '../i18n'
import { useAuth } from '../context/AuthContext'
import ColumnFilterMulti from '../components/ColumnFilterMulti'
import ChecklistDropdown from '../components/ChecklistDropdown'
import MultiSelectDropdown from '../components/MultiSelectDropdown'
import CopiarFormatoSap from '../components/purchases/CopiarFormatoSap'
import { Importador } from '../components/compras/ImportadorSap'
import { ControlZoom, ZonaZoom } from '../components/ot/zoom'
import { useZoom } from '../components/ot/useZoom'

// Compras v2 > Pedidos SAP. SAP owns the order: everything here is read-only
// and shown as SAP sends it (docs/compras-v2/BRIEF.md §8, §12).

const ESTADOS = ['emitido', 'recibido_parcial', 'recibido', 'cancelado']
const ESTADO_STYLE = {
  emitido: 'bg-indigo-100 text-indigo-700',
  recibido_parcial: 'bg-amber-100 text-amber-700',
  recibido: 'bg-emerald-100 text-emerald-700',
  cancelado: 'bg-gray-200 text-gray-600',
}

const HEADER_FIELDS = [
  'pedido_id', 'pedido_num', 'fecha_doc', 'fecha_entrega_doc', 'fecha_actualizacion', 'estado_doc', 'proveedor_cod',
  'proveedor', 'proyectos_rem_txt', 'condicion_pago', 'estado_compra', 'metodo_pago', 'lead_time_proveedor', 'courier', 'tracking_number',
  'moneda_doc', 'tipo_cambio', 'creado_por', 'ref_proveedor', 'comentarios', 'facturado_clp', 'factura_pagada_clp',
]
const LINE_FIELDS = [
  'item_cod', 'descripcion', 'cantidad', 'unidad', 'cant_recibida', 'precio', 'moneda', 'total_moneda_origen',
  'total_clp', 'proyecto', 'proyecto_nombre', 'proyecto_rem_txt', 'centro_costo', 'fecha_entrega_linea', 'estado_linea',
  'fecha_ult_entrada', 'cancelado', 'solicitud_rempmo',
]
const MONEY_FIELDS = new Set(['total_clp', 'facturado_clp', 'factura_pagada_clp'])

const num = (v, max = 2) => new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: max }).format(v)
const clp = (v) => (v == null ? '—' : `$${num(v, 0)}`)

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString(currentLocale(), {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}
function daysSince(iso) {
  if (!iso) return null
  const t = new Date(iso.endsWith('Z') ? iso : `${iso}Z`).getTime()
  return Math.floor((Date.now() - t) / 86400000)
}

// A SAP value, as SAP sent it: an empty one is a dash.
function valor(key, v) {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Y' : 'N'
  if (/^fecha_/.test(key)) return fmtDate(v)
  if (MONEY_FIELDS.has(key)) return clp(v)
  if (typeof v === 'number') return num(v, 4)
  return String(v)
}

function Pill({ estado, t }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_STYLE[estado] || 'bg-gray-100 text-gray-600'}`}>
      {t(`estado.${estado}`)}
    </span>
  )
}

function Badge({ children, tone = 'gray' }) {
  const tones = { gray: 'bg-gray-100 text-gray-600', amber: 'bg-amber-50 text-amber-700', green: 'bg-emerald-50 text-emerald-700', blue: 'bg-sky-50 text-sky-700' }
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${tones[tone]}`}>{children}</span>
}

// ── Side panel ─────────────────────────────────────────────────────────────
function Campos({ fields, data, t }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
      {fields.map(k => (
        <div key={k} className={k === 'comentarios' ? 'sm:col-span-2' : ''}>
          <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t(`fields.${k}`)}</dt>
          <dd className="text-sm text-gray-800 break-words">{valor(k, data[k])}</dd>
        </div>
      ))}
    </dl>
  )
}

// A boxed value, like a field of SAP's order screen.
function CampoSap({ label, children, className = '' }) {
  return (
    <div className={`grid grid-cols-[9rem_1fr] items-center gap-2 text-[13px] ${className}`}>
      <span className="text-gray-700">{label}</span>
      <span className="min-h-[1.5rem] border border-gray-300 bg-white px-2 py-0.5 text-gray-900 truncate">{children ?? ''}</span>
    </div>
  )
}

// The order as SAP's "Pedido" window shows it: header, tabs (Contenido / Logística / Finanzas), the line grid and the totals.
function Detalle({ pedidoId, onClose, t }) {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const [pestana, setPestana] = useState('contenido')
  useEffect(() => {
    let vivo = true
    getComprasPedido(pedidoId).then(r => vivo && setD(r)).catch(e => vivo && setErr(e.response?.data?.detail || e.message))
    return () => { vivo = false }
  }, [pedidoId])

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const indicador = d?.formato_sap?.indicador
  const pestanas = ['contenido', 'logistica', 'finanzas', 'otros']
  const fecha = (iso) => (iso ? fmtDate(iso) : '')

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative w-full max-w-6xl bg-[#f4f4f4] shadow-xl overflow-y-auto" role="dialog" aria-label={t('detail.title', { num: d?.pedido_num ?? pedidoId })}>
        <header className="sticky top-0 z-10 bg-[#2c4a6b] text-white px-4 py-2 flex items-center justify-between">
          <h2 className="text-base font-medium">{t('detail.sap.window')}</h2>
          <button onClick={onClose} className="text-sm text-white/80 hover:text-white px-2">{t('detail.close')} ✕</button>
        </header>
        <div className="p-4 space-y-4">
          {err && <p className="text-sm text-red-600">{err}</p>}
          {!d && !err && <p className="text-sm text-gray-400">{t('loading')}</p>}
          {d && (
            <>
              <div className="grid md:grid-cols-2 gap-x-12 gap-y-1">
                <div className="space-y-1">
                  <CampoSap label={t('detail.sap.proveedor')}>{d.proveedor_cod}</CampoSap>
                  <CampoSap label={t('detail.sap.nombre')}>{d.proveedor}</CampoSap>
                  <CampoSap label={t('detail.sap.refAcreedor')}>{d.ref_proveedor}</CampoSap>
                  <CampoSap label={t('detail.sap.moneda')}>{d.moneda_doc}{d.tipo_cambio ? ` · ${num(d.tipo_cambio, 4)}` : ''}</CampoSap>
                </div>
                <div className="space-y-1">
                  <CampoSap label={t('detail.sap.numero')}><strong>OC {d.pedido_num}</strong></CampoSap>
                  <CampoSap label={t('detail.sap.estado')}>{d.estado_doc === 'C' ? t('detail.sap.cerrado') : t('detail.sap.abierto')}</CampoSap>
                  <CampoSap label={t('detail.sap.fechaDoc')}>{fecha(d.fecha_doc)}</CampoSap>
                  <CampoSap label={t('detail.sap.fechaEntrega')}>{fecha(d.fecha_entrega_doc)}</CampoSap>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <Pill estado={d.estado_derivado} t={t} />
                {d.a_credito && <Badge tone="amber">{t('badges.credito')}</Badge>}
                {d.pagado && <Badge tone="green">{t('badges.pagado')}</Badge>}
                {d.estado_compra && <Badge tone="blue">{d.estado_compra}</Badge>}
                <span className="text-xs text-gray-500">{t('detail.linkedRequests')}: {d.solicitudes.length ? d.solicitudes.join(', ') : t('detail.noLinked')}</span>
              </div>

              <div>
                <div className="flex border-b border-gray-300" role="tablist">
                  {pestanas.map(k => (
                    <button key={k} type="button" role="tab" aria-selected={pestana === k} onClick={() => setPestana(k)}
                      className={`px-5 py-1.5 text-[13px] border border-b-0 -mb-px ${pestana === k ? 'bg-white border-gray-300 text-gray-900 font-medium' : 'bg-gray-200 border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                      {t(`detail.sap.tab.${k}`)}
                    </button>
                  ))}
                </div>
                <div className="bg-white border border-gray-300 border-t-0 p-3 min-h-[16rem]">
                  {pestana === 'contenido' && (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="text-xs text-gray-500">{t('detail.sap.lineas', { count: d.lineas.length })}</span>
                        {d.formato_sap && (
                          <CopiarFormatoSap texto={d.formato_sap.texto} hecho={t('detail.sap.copiado')}>⧉ {t('detail.sap.copiar')}</CopiarFormatoSap>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-[13px] border-collapse">
                          <thead>
                            <tr className="bg-gray-100 text-left text-gray-700">
                              {['num', 'articulo', 'descripcion', 'cantidad', 'precio', 'dcto', 'impuesto', 'total', 'proyecto', 'areas'].map(c => (
                                <th key={c} className={`border border-gray-300 px-2 py-1 font-normal whitespace-nowrap ${['cantidad', 'precio', 'dcto', 'total'].includes(c) ? 'text-right' : ''}`}>{t(`detail.sap.col.${c}`)}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {d.lineas.map(l => (
                              <tr key={l.linea_num} className={l.cancelado ? 'text-gray-400 line-through' : ''}>
                                <td className="border border-gray-200 px-2 py-1 text-gray-400">{l.linea_num}</td>
                                <td className="border border-gray-200 px-2 py-1 font-mono">{l.item_cod || ''}</td>
                                <td className="border border-gray-200 px-2 py-1">{l.descripcion}</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">{l.cantidad != null ? num(l.cantidad, 4) : ''}{l.unidad ? ` ${l.unidad}` : ''}</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">{l.precio != null ? num(l.precio, 4) : ''}</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">0,0000</td>
                                <td className="border border-gray-200 px-2 py-1 font-mono">{indicador || ''}</td>
                                <td className="border border-gray-200 px-2 py-1 text-right">{l.total_clp != null ? clp(l.total_clp) : ''}</td>
                                <td className="border border-gray-200 px-2 py-1 whitespace-nowrap">{l.proyecto}{l.proyecto_rem_txt ? <span className="text-gray-400"> · {l.proyecto_rem_txt}</span> : null}</td>
                                <td className="border border-gray-200 px-2 py-1">{l.centro_costo}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {d.formato_sap && <p className="text-xs text-gray-400 mt-2">{t('detail.sap.copiarNota', { indicador })}</p>}
                    </>
                  )}
                  {pestana === 'logistica' && (
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-1 max-w-4xl">
                      <CampoSap label={t('fields.courier')}>{d.courier}</CampoSap>
                      <CampoSap label={t('fields.tracking_number')}>{d.tracking_number}</CampoSap>
                      <CampoSap label={t('fields.lead_time_proveedor')}>{d.lead_time_proveedor}</CampoSap>
                      <CampoSap label={t('fields.estado_compra')}>{d.estado_compra}</CampoSap>
                      <CampoSap label={t('fields.fecha_actualizacion')}>{fecha(d.fecha_actualizacion)}</CampoSap>
                      <CampoSap label={t('fields.creado_por')}>{d.creado_por}</CampoSap>
                    </div>
                  )}
                  {pestana === 'finanzas' && (
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-1 max-w-4xl">
                      <CampoSap label={t('fields.condicion_pago')}>{d.condicion_pago}</CampoSap>
                      <CampoSap label={t('fields.metodo_pago')}>{d.metodo_pago}</CampoSap>
                      <CampoSap label={t('fields.facturado_clp')}>{d.facturado_clp != null ? clp(d.facturado_clp) : ''}</CampoSap>
                      <CampoSap label={t('fields.factura_pagada_clp')}>{d.factura_pagada_clp != null ? clp(d.factura_pagada_clp) : ''}</CampoSap>
                    </div>
                  )}
                  {pestana === 'otros' && (
                    <>
                      <Campos fields={HEADER_FIELDS} data={d} t={t} />
                      <p className="text-xs text-gray-400 mt-3">{t('detail.note')}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <div className="w-full max-w-sm space-y-1">
                  <CampoSap label={t('detail.sap.totalDoc')}><span className="block text-right font-medium">{d.total_clp != null ? clp(d.total_clp) : ''}</span></CampoSap>
                  <CampoSap label={t('fields.facturado_clp')}><span className="block text-right">{d.facturado_clp != null ? clp(d.facturado_clp) : ''}</span></CampoSap>
                  <CampoSap label={t('fields.factura_pagada_clp')}><span className="block text-right">{d.factura_pagada_clp != null ? clp(d.factura_pagada_clp) : ''}</span></CampoSap>
                </div>
              </div>
              {d.comentarios && <CampoSap label={t('fields.comentarios')} className="max-w-3xl">{d.comentarios}</CampoSap>}
            </>
          )}
        </div>
      </aside>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────
const SIN_ESTADO = '__sin_estado__'
// SAP's EstadoCompra values, in the order they are worked (unknown ones go after them).
const COMPRA_ORDEN = ['PENDIENTE DE PAGO', 'FORMULARIO ENVIADO', 'PAGADO', 'PAGADO CON SALDO A FAVOR', 'PAUSADO', 'CERRAR']
const SORT_INICIAL = { fecha: 'desc', numero: 'desc', total: 'desc', descripcion: 'asc', proyecto: 'asc', proveedor: 'asc', estado: 'asc' }
const PAGE_SIZES = [25, 50, 100, 200, 500]
const ORIGENES = ['nacional', 'extranjero', 'otro']

const lista = (s) => (s ? s.split('|').filter(Boolean) : [])

function tamanoGuardado() {
  try {
    const n = Number(localStorage.getItem('compras_pedidos_por_pagina'))
    return PAGE_SIZES.includes(n) ? n : 50
  } catch { return 50 }
}

function Kpi({ label, hint, n, total, tone, t }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3" title={hint}>
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold ${tone}`}>{total == null ? num(n, 0) : clp(total)}</div>
      <div className="text-xs text-gray-400">{t('count', { n: num(n, 0) })}</div>
    </div>
  )
}

export default function ComprasPedidos() {
  const zoom = useZoom('adq_zoom_pedidos')
  const { t } = useTranslation(['compras', 'common'])
  const [sp, setSp] = useSearchParams()
  const { user } = useAuth()
  const esAdmin = (user?.roles || []).includes('admin')
  const [estadoApp, setEstadoApp] = useState(null)
  const [filtros, setFiltros] = useState({ proyectos: [], proveedores: [], creado_por: [], estados_compra: [] })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [importando, setImportando] = useState(false)
  const [recarga, setRecarga] = useState(0)   // bumped after an import
  const [qLocal, setQLocal] = useState(sp.get('q') || '')
  const [seleccion, setSeleccion] = useState(() => new Set())     // ticked orders; they survive paging and filtering
  const [exportando, setExportando] = useState(false)

  // Filters, order and page live in the URL, so a view can be shared and survives a reload.
  const q = sp.get('q') || ''
  const estado = lista(sp.get('estado'))
  const oculta = lista(sp.get('oculta'))               // SAP purchase statuses left unchecked (hidden)
  const proyecto = lista(sp.get('proyecto'))
  const proveedor = lista(sp.get('proveedor'))
  const origen = lista(sp.get('origen'))                   // nacional | extranjero | otro (SAP codes: PN... / PE...)
  const creador = lista(sp.get('creador'))                 // who created the order in SAP
  const hasta = /^\d{4}-\d{2}-\d{2}$/.test(sp.get('hasta') || '') ? sp.get('hasta') : ''   // only orders dated up to this day (the tray: older than 90 days)
  const pago = sp.get('pago') || ''                       // credito | contado
  const abiertos = sp.get('abiertos') !== '0'             // on unless turned off
  const sort = sp.get('sort') || 'fecha'
  const dir = sp.get('dir') || SORT_INICIAL[sort] || 'desc'
  const page = Math.max(1, Number(sp.get('page')) || 1)
  const size = PAGE_SIZES.includes(Number(sp.get('size'))) ? Number(sp.get('size')) : tamanoGuardado()
  const abierto = sp.get('pedido') ? Number(sp.get('pedido')) : null

  const setParam = useCallback((cambios) => {
    const n = new URLSearchParams(sp)
    Object.entries(cambios).forEach(([k, v]) => {
      const val = Array.isArray(v) ? v.join('|') : v
      if (val) n.set(k, val); else n.delete(k)
    })
    if (!('page' in cambios) && !('pedido' in cambios)) n.delete('page')
    setSp(n, { replace: true })
  }, [sp, setSp])

  useEffect(() => {
    const h = setTimeout(() => { if (qLocal !== q) setParam({ q: qLocal }) }, 300)
    return () => clearTimeout(h)
  }, [qLocal]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getComprasEstado().then(setEstadoApp).catch(() => {})
    getComprasFiltros().then(setFiltros).catch(() => {})
  }, [recarga])

  const params = useMemo(() => {
    const p = { page, page_size: size, sort, dir }
    if (q) p.q = q
    if (estado.length) p.estado = estado
    if (oculta.length) p.ocultar_compra = oculta
    if (proyecto.length) p.proyecto = proyecto
    if (proveedor.length) p.proveedor = proveedor
    if (origen.length) p.origen_proveedor = origen
    if (creador.length) p.creado_por = creador
    if (hasta) p.hasta = hasta
    if (pago) p.credito = pago === 'credito'
    if (abiertos) p.abiertos = true
    return p
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sp.get('estado'), sp.get('oculta'), sp.get('proyecto'), sp.get('proveedor'), sp.get('origen'), sp.get('creador'), hasta, pago, abiertos, sort, dir, page, size])

  // The previous page stays on screen while the next one loads.
  useEffect(() => {
    let vivo = true
    getComprasPedidos(params).then(r => { if (vivo) { setData(r); setErr('') } })
      .catch(e => { if (vivo) setErr(e.response?.data?.detail || e.message) })
      .finally(() => { if (vivo) setLoading(false) })
    return () => { vivo = false }
  }, [params, recarga])

  const cambiaTamano = (n) => {
    try { localStorage.setItem('compras_pedidos_por_pagina', String(n)) } catch { /* private window */ }
    setParam({ size: String(n) })
  }
  const ordenar = (k) => setParam(sort === k ? { sort: k, dir: dir === 'asc' ? 'desc' : 'asc' } : { sort: k, dir: SORT_INICIAL[k] })
  const alternar = (clave, actuales, v) => setParam({ [clave]: actuales.includes(v) ? actuales.filter(x => x !== v) : [...actuales, v] })

  const hayFiltros = q || estado.length || oculta.length || proyecto.length || proveedor.length || origen.length || creador.length || hasta || pago || !abiertos
  const limpiar = () => { setQLocal(''); setSp(new URLSearchParams(), { replace: true }) }

  const ult = estadoApp?.ultima_importacion
  const dias = ult ? daysSince(ult.creada_en) : null
  const pages = data ? Math.max(1, Math.ceil(data.total / size)) : 1
  const montos = data ? data.ve_montos : true
  const resumen = data?.resumen

  // Everyone who has created an order in SAP, with how many each one has under the other filters.
  const opcionesCreador = useMemo(() => {
    const nombres = new Set([...(filtros.creado_por || []), ...Object.keys(data?.conteos_creador || {}), ...creador])
    return [...nombres].sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v, detail: String(data?.conteos_creador?.[v] ?? 0) }))
  }, [filtros.creado_por, data?.conteos_creador, creador])

  const estadosCompra = useMemo(() => {
    const vistos = new Set([...(filtros.estados_compra || []), ...Object.keys(data?.conteos_compra || {}), ...oculta])
    vistos.delete(SIN_ESTADO)
    const conocidos = COMPRA_ORDEN.filter(x => vistos.has(x))
    const otros = [...vistos].filter(x => !COMPRA_ORDEN.includes(x)).sort()
    return [...conocidos, ...otros, SIN_ESTADO]
  }, [filtros.estados_compra, data?.conteos_compra, oculta])

  const filtroTxt = {
    searchPlaceholder: t('filterSearch'), clearLabel: t('filterClear'), emptyLabel: t('noOptions'),
  }
  const opcionesEstado = ESTADOS.map(e => ({ value: e, label: t(`estado.${e}`) }))
  // REM-PMO's project when SAP's code is linked to one, else SAP's own name (and a warning that it is not linked).
  const opcionesProyecto = filtros.proyectos.map(p => ({
    value: p.codigo,
    label: p.rem ? `${p.rem.name} · ${p.codigo}` : `${p.codigo}${p.nombre ? ` · ${p.nombre}` : ''} (${t('badges.sinEnlazar').toLowerCase()})`,
  }))
  const sinEnlazar = filtros.proyectos.filter(p => !p.rem)

  const orden = (k, label) => {
    const activo = sort === k
    return (
      <button type="button" onClick={() => ordenar(k)} className={`inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-gray-800 ${activo ? 'text-gray-900 font-semibold' : ''}`}
        title={t(dir === 'asc' && activo ? 'sort.desc' : 'sort.asc', { col: label })}>
        {label}
        <span className={activo ? '' : 'invisible'} aria-hidden="true">{activo && dir === 'asc' ? '▲' : '▼'}</span>
      </button>
    )
  }
  const ariaSort = (k) => (sort === k ? (dir === 'asc' ? 'ascending' : 'descending') : 'none')

  const idsPagina = (data?.pedidos || []).map(p => p.pedido_id)
  const marcar = (id) => setSeleccion(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const marcarPagina = (on) => setSeleccion(s => { const n = new Set(s); idsPagina.forEach(id => (on ? n.add(id) : n.delete(id))); return n })
  // Ticked orders go as they are; with none ticked, everything that meets the filters.
  const exportar = async () => {
    setExportando(true); setErr('')
    try {
      const { page: _p, page_size: _s, ...filtrosActuales } = params
      await exportComprasPedidos(filtrosActuales, [...seleccion])
    } catch (e) {
      setErr(e.response?.status === 404 ? t('export.empty') : t('export.failed'))
    }
    setExportando(false)
  }

  const pager = () => (
    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
      <label className="flex items-center gap-1.5">
        {t('perPage')}
        <select value={size} onChange={e => cambiaTamano(Number(e.target.value))} className="rounded-md border border-gray-300 px-1.5 py-1 text-sm text-gray-700">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <div className="flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => setParam({ page: String(page - 1) })} className="px-3 py-1 rounded border disabled:opacity-40">{t('prev')}</button>
        <span>{t('page', { page, pages })}</span>
        <button disabled={page >= pages} onClick={() => setParam({ page: String(page + 1) })} className="px-3 py-1 rounded border disabled:opacity-40">{t('next')}</button>
      </div>
    </div>
  )

  const chip = (activo, onClick, children, title) => (
    <button type="button" onClick={onClick} title={title}
      className={`px-3 py-1 rounded-full text-sm border ${activo ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
      {children}
    </button>
  )

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('title')}</h1>
          <p className="text-sm text-gray-500">{t('subtitle')}</p>
        </div>
        {estadoApp?.puede_importar && (
          <button onClick={() => setImportando(true)}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
            ↑ {t('import.button')}
          </button>
        )}
      </header>

      {/* "Datos SAP al ..." banner (brief §9.4) */}
      <div className={`rounded-lg px-4 py-2 text-sm flex flex-wrap items-center gap-x-3 ${ult ? (dias >= 2 ? 'bg-amber-50 text-amber-800' : 'bg-sky-50 text-sky-800') : 'bg-amber-50 text-amber-800'}`}>
        {ult ? (
          <>
            <strong>{t('banner.dataAt', { date: fmtDate(ult.datos_al) })}</strong>
            <span>{t('banner.loaded', { when: fmtDateTime(ult.creada_en), who: ult.usuario || '—' })}</span>
            {dias >= 2 && <span>· {t('banner.stale', { days: dias })}</span>}
          </>
        ) : t('banner.never')}
      </div>

      {/* SAP projects with no REM-PMO project: they are linked by the SAP code in Administración > Proyectos */}
      {sinEnlazar.length > 0 && (
        <div className="rounded-lg px-4 py-2 text-sm bg-amber-50 text-amber-800 flex flex-wrap items-center gap-x-3">
          <span>
            {t('banner.unlinked', { count: sinEnlazar.length })}{' '}
            <span className="font-mono text-xs" title={sinEnlazar.map(p => `${p.codigo}${p.nombre ? ` · ${p.nombre}` : ''}`).join('\n')}>
              {sinEnlazar.slice(0, 6).map(p => p.codigo).join(', ')}{sinEnlazar.length > 6 ? '…' : ''}
            </span>
          </span>
          {esAdmin && <Link to="/admin/proyectos" className="underline font-medium">{t('banner.unlinkedGo')}</Link>}
        </div>
      )}

      {/* Sums of what is on screen */}
      {resumen && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi t={t} label={t('kpi.shown')} hint={t('kpi.shownHint')} n={resumen.todos.n} total={resumen.todos.total_clp} tone="text-gray-900" />
          <Kpi t={t} label={t('kpi.toReceive')} hint={t('kpi.toReceiveHint')} n={resumen.pendiente_recepcion.n} total={resumen.pendiente_recepcion.total_clp} tone="text-indigo-700" />
          <Kpi t={t} label={t('kpi.toPay')} hint={t('kpi.toPayHint')} n={resumen.por_pagar.n} total={resumen.por_pagar.total_clp} tone="text-amber-700" />
        </div>
      )}

      {/* Search and quick filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <input value={qLocal} onChange={e => setQLocal(e.target.value)} placeholder={t('search')} aria-label={t('search')}
          className="flex-1 min-w-[16rem] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        <select value={pago} onChange={e => setParam({ pago: e.target.value })} aria-label={t('filters.payment')}
          className="rounded-lg border border-gray-300 px-2 py-2 text-sm">
          <option value="">{t('filters.payment')}: {t('filters.paymentAny')}</option>
          <option value="credito">{t('filters.credit')}</option>
          <option value="contado">{t('filters.cash')}</option>
        </select>
        <MultiSelectDropdown
          label={t('filters.buyer')} values={creador} options={opcionesCreador} onChange={v => setParam({ creador: v })}
          allLabel={t('filters.all')} searchPlaceholder={t('filterSearch')} clearLabel={t('filterClear')}
          onlyLabel={t('chips.only')} emptyLabel={t('noOptions')} />
        <ChecklistDropdown
          label={t('chips.purchaseState')} hidden={oculta} onChange={v => setParam({ oculta: v })}
          allLabel={t('chips.checkAll')} noneLabel={t('chips.checkNone')} onlyLabel={t('chips.only')}
          options={estadosCompra.map(v => {
            const c = data?.conteos_compra?.[v]
            return { value: v, label: v === SIN_ESTADO ? t('chips.none') : v,
              detail: `${c?.n ?? 0}${montos && c?.total_clp != null ? ` · ${clp(c.total_clp)}` : ''}` }
          })} />
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={abiertos} onChange={e => setParam({ abiertos: e.target.checked ? '' : '0' })} />
          {t('filters.onlyOpen')}
        </label>
        {hasta && (
          <button type="button" onClick={() => setParam({ hasta: '' })} title={t('filters.untilClear')}
            className="px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-gray-200">{t('filters.until', { date: fmtDate(hasta) })} ✕</button>
        )}
        {hayFiltros && <button onClick={limpiar} className="text-sm text-indigo-600 hover:underline">{t('filters.clear')}</button>}
      </div>

      {/* Chips: the order's state here, and SAP's own purchase status */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-36">{t('chips.orderState')}</span>
          {chip(estado.length === 0, () => setParam({ estado: [] }), <>{t('estado.all')}</>)}
          {ESTADOS.map(e => chip(estado.includes(e), () => alternar('estado', estado, e),
            <>{t(`estado.${e}`)} <span className={estado.includes(e) ? 'text-gray-300' : 'text-gray-400'}>{data?.conteos?.[e] ?? 0}</span></>))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-36">{t("chips.supplierType")}</span>
          {chip(origen.length === 0, () => setParam({ origen: [] }), <>{t("estado.all")}</>)}
          {ORIGENES.map(o => chip(origen.includes(o), () => alternar("origen", origen, o),
            <>{t(`origen.${o}`)} <span className={origen.includes(o) ? "text-gray-300" : "text-gray-400"}>{data?.conteos_origen?.[o] ?? 0}</span></>))}
        </div>
        {oculta.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <span className="text-xs font-medium w-36">{t("chips.hiding")}</span>
            {oculta.map(v => (
              <button key={v} type="button" onClick={() => setParam({ oculta: oculta.filter(x => x !== v) })} title={t("chips.show")}
                className="px-2.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 hover:bg-gray-200">
                {v === SIN_ESTADO ? t("chips.none") : v} ✕
              </button>
            ))}
            <button type="button" onClick={() => setParam({ oculta: [] })} className="text-xs text-indigo-600 hover:underline">{t("chips.showAll")}</button>
          </div>
        )}
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}
      {data && montos === false && <p className="text-xs text-gray-400">{t('noAmounts')}</p>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-500">{data ? t('count', { n: num(data.total, 0) }) : ''}</span>
          {seleccion.size > 0 && (
            <span className="text-sm text-indigo-700">
              {t('export.selected', { n: seleccion.size })}{' '}
              <button type="button" onClick={() => setSeleccion(new Set())} className="underline">{t('export.clear')}</button>
            </span>
          )}
          <button type="button" onClick={exportar} disabled={exportando || !data || data.total === 0}
            title={seleccion.size ? t('export.hintSelected') : t('export.hintAll')}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
            ↓ {exportando ? t('export.working') : seleccion.size ? t('export.buttonSelected', { n: seleccion.size }) : t('export.buttonAll', { n: num(data?.total ?? 0, 0) })}
          </button>
        </div>
        <div className="flex items-center gap-3"><ControlZoom z={zoom} />{pager()}</div>
      </div>

      <ZonaZoom z={zoom} libre>
      {/* List: six columns at most; sort and filter from the headers */}
      <div className="bg-white rounded-xl border overflow-x-auto lg:overflow-visible">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="pl-3 pr-0 py-2 w-8">
                <input type="checkbox" className="accent-gray-900" aria-label={t('export.selectPage')} title={t('export.selectPage')}
                  checked={idsPagina.length > 0 && idsPagina.every(id => seleccion.has(id))}
                  ref={el => { if (el) el.indeterminate = idsPagina.some(id => seleccion.has(id)) && !idsPagina.every(id => seleccion.has(id)) }}
                  onChange={e => marcarPagina(e.target.checked)} />
              </th>
              <th className="px-3 py-2" aria-sort={ariaSort(sort === 'numero' ? 'numero' : 'fecha')}>
                <span className="inline-flex items-center gap-2">
                  {orden('fecha', t('cols.date'))}
                  {orden('numero', t('cols.number'))}
                </span>
                <ColumnFilterMulti label={t("filterBy", { col: t("filters.buyer") })} values={creador} options={opcionesCreador} onChange={v => setParam({ creador: v })} {...filtroTxt} />
              </th>
              <th className="px-3 py-2" aria-sort={ariaSort('descripcion')}>{orden('descripcion', t('cols.what'))}</th>
              <th className="px-3 py-2 whitespace-nowrap" aria-sort={ariaSort('proyecto')}>
                {orden('proyecto', t('cols.project'))}
                <ColumnFilterMulti label={t('filterBy', { col: t('cols.project') })} values={proyecto} options={opcionesProyecto} onChange={v => setParam({ proyecto: v })} {...filtroTxt} />
              </th>
              <th className="px-3 py-2 whitespace-nowrap" aria-sort={ariaSort('proveedor')}>
                {orden('proveedor', t('cols.supplier'))}
                <ColumnFilterMulti label={t('filterBy', { col: t('cols.supplier') })} values={proveedor} options={filtros.proveedores} onChange={v => setParam({ proveedor: v })} {...filtroTxt} />
              </th>
              <th className="px-3 py-2 whitespace-nowrap" aria-sort={ariaSort('estado')}>
                {orden('estado', t('cols.status'))}
                <ColumnFilterMulti label={t('filterBy', { col: t('cols.status') })} values={estado} options={opcionesEstado} onChange={v => setParam({ estado: v })} {...filtroTxt} />
              </th>
              <th className="px-3 py-2 text-right whitespace-nowrap" aria-sort={ariaSort('total')}>{montos && orden('total', t('cols.total'))}{!montos && t('cols.total')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">{t('loading')}</td></tr>}
            {data && data.pedidos.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-500">
                {estadoApp && estadoApp.pedidos === 0 ? t('emptyNone') : t('empty')}
              </td></tr>
            )}
            {data?.pedidos.map(p => (
              <tr key={p.pedido_id} onClick={() => setParam({ pedido: p.pedido_id })} tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') setParam({ pedido: p.pedido_id }) }}
                className={`border-t hover:bg-indigo-50/40 cursor-pointer focus:outline-none focus:bg-indigo-50/60 ${seleccion.has(p.pedido_id) ? 'bg-indigo-50/60' : ''}`}>
                <td className="pl-3 pr-0 py-2 w-8" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                  <input type="checkbox" className="accent-gray-900" checked={seleccion.has(p.pedido_id)}
                    aria-label={t('export.selectOne', { num: p.pedido_num ?? p.pedido_id })}
                    onChange={() => marcar(p.pedido_id)} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{p.pedido_num ?? p.pedido_id}</div>
                  <div className="text-xs text-gray-400">{fmtDate(p.fecha_doc)}</div>
                  {p.creado_por && <div className="text-xs text-gray-400 max-w-[9rem] truncate" title={`${t("filters.buyer")}: ${p.creado_por}`}>{p.creado_por}</div>}
                </td>
                <td className="px-3 py-2 max-w-[22rem]">
                  <div className="truncate text-gray-800" title={p.descripcion}>{p.descripcion}</div>
                  {p.mas_lineas > 0 && <div className="text-xs text-gray-400">{t('moreLines', { n: p.mas_lineas })}</div>}
                </td>
                <td className="px-3 py-2 text-gray-700 max-w-[13rem]">
                  {p.proyectos_rem.length === 0 ? '—' : p.proyectos_rem.length === 1 ? (
                    <>
                      <div className="truncate" title={p.proyectos_rem[0].rem ? `${p.proyectos_rem[0].rem.code} · ${p.proyectos_rem[0].rem.name}` : undefined}>
                        {p.proyectos_rem[0].rem ? p.proyectos_rem[0].rem.name : p.proyectos_rem[0].sap}
                      </div>
                      {p.proyectos_rem[0].rem
                        ? <div className="text-xs text-gray-400">{p.proyectos_rem[0].sap}</div>
                        : <Badge tone="amber">{t('badges.sinEnlazar')}</Badge>}
                    </>
                  ) : (
                    <>
                      <div title={p.proyectos_rem.map(r => (r.rem ? r.rem.name : r.sap)).join(', ')}>{t('multiProject', { n: p.proyectos_rem.length })}</div>
                      {p.proyecto_sin_enlazar && <Badge tone="amber">{t('badges.sinEnlazar')}</Badge>}
                    </>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[14rem] text-gray-700">
                  <div className="truncate" title={p.proveedor}>{p.proveedor || '—'}</div>
                  {p.origen_proveedor === 'extranjero' && <Badge tone="blue">{t('origen.extranjeroCorto')}</Badge>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <Pill estado={p.estado_derivado} t={t} />
                    {p.a_credito && <Badge tone="amber">{t('badges.credito')}</Badge>}
                    {p.pagado && <Badge tone="green">{t('badges.pagado')}</Badge>}
                  </div>
                  {p.estado_compra && <div className="text-xs text-gray-400 mt-0.5">{p.estado_compra}</div>}
                  {(p.courier || p.tracking_number) && (
                    <div className="text-xs text-gray-400 mt-0.5 max-w-[14rem] truncate" title={`${p.courier || ''} ${p.tracking_number || ''}`}>
                      🚚 {[p.courier, p.tracking_number].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-gray-800" title={p.moneda_doc && p.moneda_doc !== 'CLP' ? `${t('fields.moneda_doc')}: ${p.moneda_doc}` : undefined}>
                  {clp(p.total_clp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ZonaZoom>

      {data && data.total > 0 && <div className="flex justify-end">{pager()}</div>}

      {abierto && <Detalle key={abierto} pedidoId={abierto} onClose={() => setParam({ pedido: '' })} t={t} />}
      {importando && <Importador onClose={() => setImportando(false)} onDone={() => setRecarga(n => n + 1)} t={t} />}
    </div>
  )
}
